/**
 * tour-runner.ts
 *
 * Driver.js を使ったプロダクトツアーのランタイム。
 * Nuxt.js (Vue Router) 環境向けに調整。
 *
 * 使い方（Nuxt プラグインから呼び出す場合）:
 *   import { startTour } from '@/tours/tour-runner';
 *   import { dashboardTourSteps } from '@/tours/dashboard-tour';
 *   startTour({ tourId: 'dashboard', steps: dashboardTourSteps });
 *
 * URLパラメータで起動する場合:
 *   /companies/2/dashboard?tour=dashboard
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// driver.js は yarn add driver.js でインストール済み
import { driver as createDriver } from 'driver.js';
// -------- メイン関数 --------
/**
 * ツアーを開始する。
 */
export function startTour(config) {
    var instance = buildTourInstance(config);
    instance.drive();
}
// -------- 内部ヘルパー --------
function buildTourInstance(config) {
    // eslint-disable-next-line prefer-const
    var instance;
    // 現在のステップのイベントリスナーを管理する AbortController。
    // ステップが変わるたびに abort して前のリスナーを一括削除する。
    var stepAbortController = null;
    // 現在アクティブなステップの afterLeave。ステップ遷移 or destroy 時に呼ぶ。
    var currentAfterLeave = null;
    var steps = config.steps.map(function (step, index) {
        var _a;
        var nextEl = (_a = config.steps[index + 1]) === null || _a === void 0 ? void 0 : _a.element;
        // 次ステップへ進む共通処理（次要素の出現を待ってから moveNext）
        var advance = function () {
            stepAbortController === null || stepAbortController === void 0 ? void 0 : stepAbortController.abort();
            waitForElement(nextEl, timeoutSignal(5000)).then(function () { return instance.moveNext(); });
        };
        var builtStep = {
            element: step.element,
            popover: __assign({}, step.popover)
        };
        // nextTriggerSelector: 「次へ」押下時に指定要素をクリックしてから進む
        // onNextClick は DriveStep ではなく Popover のフィールドなので popover に設定する
        if (step.nextTriggerSelector) {
            builtStep.popover = __assign(__assign({}, builtStep.popover), { onNextClick: function () {
                    // 手動 Next が押された時点で auto-advance リスナーを無効化し二重進行を防ぐ
                    stepAbortController === null || stepAbortController === void 0 ? void 0 : stepAbortController.abort();
                    var trigger = document.querySelector(step.nextTriggerSelector);
                    if (trigger) {
                        trigger.click();
                        waitForElement(nextEl, timeoutSignal(5000)).then(function () { return instance.moveNext(); });
                    }
                    else {
                        instance.moveNext();
                    }
                } });
        }
        // afterLeave: ステップ離脱時に onDeselected 経由で呼ぶ
        if (step.afterLeave) {
            builtStep.onDeselected = function () {
                currentAfterLeave = null;
                step.afterLeave();
            };
        }
        // onHighlighted: autoAdvanceTrigger と afterHighlight と afterLeave 追跡を合成
        var needsHighlighted = (step.autoAdvanceTrigger && step.autoAdvanceTrigger !== 'manual') ||
            !!step.afterHighlight ||
            !!step.afterLeave;
        if (needsHighlighted) {
            builtStep.onHighlighted = function () {
                var _a, _b;
                // destroy 時にクリーンアップできるよう現ステップの afterLeave を保持
                currentAfterLeave = (_a = step.afterLeave) !== null && _a !== void 0 ? _a : null;
                // autoAdvanceTrigger ロジック
                if (step.autoAdvanceTrigger && step.autoAdvanceTrigger !== 'manual') {
                    stepAbortController === null || stepAbortController === void 0 ? void 0 : stepAbortController.abort();
                    stepAbortController = new AbortController();
                    var el = document.querySelector(step.element);
                    if (el) {
                        autoAdvanceHandlers[step.autoAdvanceTrigger](el, advance, stepAbortController.signal);
                    }
                }
                // 追加コールバック（ポップオーバー位置補正など）
                (_b = step.afterHighlight) === null || _b === void 0 ? void 0 : _b.call(step);
            };
        }
        return builtStep;
    });
    instance = createDriver({
        showProgress: true,
        animate: true,
        overlayOpacity: 0.5,
        // グレーのオーバーレイ部分をクリックしてもツアーが閉じないようにする
        overlayClickBehavior: function () {
            /* no-op */
        },
        steps: steps,
        // ステップ表示開始時：onStart と onStepChange を Vuex へ通知
        onHighlightStarted: function (_el, _step, _a) {
            var _b, _c, _d, _e, _f;
            var state = _a.state;
            var index = (_b = state.activeIndex) !== null && _b !== void 0 ? _b : 0;
            if (index === 0)
                (_d = (_c = config.callbacks) === null || _c === void 0 ? void 0 : _c.onStart) === null || _d === void 0 ? void 0 : _d.call(_c);
            (_f = (_e = config.callbacks) === null || _e === void 0 ? void 0 : _e.onStepChange) === null || _f === void 0 ? void 0 : _f.call(_e, index);
        },
        onDestroyStarted: function () {
            var _a, _b;
            stepAbortController === null || stepAbortController === void 0 ? void 0 : stepAbortController.abort();
            // destroy 時も afterLeave を呼んで副作用をクリーンアップする
            // （Driver.js は destroy 時に onDeselected を呼ばないため手動で実行）
            currentAfterLeave === null || currentAfterLeave === void 0 ? void 0 : currentAfterLeave();
            currentAfterLeave = null;
            (_b = (_a = config.callbacks) === null || _a === void 0 ? void 0 : _a.onEnd) === null || _b === void 0 ? void 0 : _b.call(_a);
            instance.destroy();
        }
    });
    return instance;
}
var autoAdvanceHandlers = {
    'button-click': function (el, advance, signal) {
        el.addEventListener('click', advance, { signal: signal, once: true });
    },
    'text-enter': function (el, advance, signal) {
        el.addEventListener('keydown', function (e) {
            var ev = e;
            var input = ev.target;
            if ((ev.key === 'Enter' || ev.key === 'Tab') && input.value.trim() !== '') {
                advance();
            }
        }, { signal: signal });
    },
    'select-change': function (el, advance, signal) {
        el.addEventListener('change', advance, { signal: signal, once: true });
    },
    'custom-dropdown': function (el, advance, signal) {
        var _a;
        // 表示テキストが変化したら選択完了とみなして進む
        var initialText = (_a = el.textContent) !== null && _a !== void 0 ? _a : '';
        var observer = new MutationObserver(function () {
            var _a;
            if (((_a = el.textContent) !== null && _a !== void 0 ? _a : '') !== initialText) {
                observer.disconnect();
                advance();
            }
        });
        observer.observe(el, { childList: true, subtree: true, characterData: true });
        // signal abort 時に MutationObserver も停止
        signal.addEventListener('abort', function () { return observer.disconnect(); }, { once: true });
    },
    'checkbox-change': function (el, advance, signal) {
        el.addEventListener('change', advance, { signal: signal, once: true });
    },
    'file-selected': function (el, advance, signal) {
        el.addEventListener('change', function (e) {
            var input = e.target;
            if (input.files && input.files.length > 0) {
                advance();
            }
        }, { signal: signal, once: true });
    },
    manual: function () {
        /* 手動進行のため何もしない */
    }
};
// -------- Phase 3-2: AbortController ベースの waitForElement --------
/**
 * 指定ミリ秒後に自動 abort する AbortSignal を生成する。
 * 呼び出し側は AbortController を保持する必要がない。
 */
function timeoutSignal(ms) {
    var controller = new AbortController();
    setTimeout(function () { return controller.abort(); }, ms);
    return controller.signal;
}
/**
 * 指定セレクタが DOM に現れるまで待機する。
 * - 既に存在する場合は即 resolve
 * - signal が abort（タイムアウト含む）された場合も resolve してツアーを続行させる
 * - cleanup は signal の abort イベントで一元管理する
 */
function waitForElement(selector, signal) {
    if (!selector)
        return Promise.resolve();
    if (document.querySelector(selector))
        return Promise.resolve();
    if (signal.aborted)
        return Promise.resolve();
    return new Promise(function (resolve) {
        var observer = new MutationObserver(function () {
            if (document.querySelector(selector)) {
                observer.disconnect();
                resolve();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        // タイムアウト or 手動キャンセル時に observer を停止して resolve（ツアー続行）
        signal.addEventListener('abort', function () {
            observer.disconnect();
            resolve();
        }, { once: true });
    });
}
